import type { ProblemDetails } from '@platform/types';

/**
 * Platform HTTP client shared by the web dashboard and the mobile app.
 * Extracted from apps/web/src/lib/api-client.ts — behaviour is identical for
 * web; the config makes the two platform differences injectable:
 *
 * - baseUrl: web uses the relative '/api/v1' (Vite proxy / same-origin);
 *   mobile must use an absolute origin.
 * - refresh-token transport: web keeps it in the httpOnly cookie (send
 *   credentials, empty refresh body); mobile has no cookie jar, so it stores
 *   the token itself (SecureStore) and supplies it via `getRefreshToken`,
 *   which switches the refresh call to body transport + the
 *   X-Client-Platform: mobile header.
 */
export interface ApiClientConfig {
  baseUrl: string;
  /** 'include' on web (refresh cookie), 'omit' on mobile. Default 'include'. */
  credentials?: RequestCredentials;
  /** Mobile only: read the stored refresh token. Presence switches the refresh call to body transport. */
  getRefreshToken?: () => Promise<string | null>;
  /** Called after a successful refresh so the host app can persist rotated tokens / update state. */
  onTokensRefreshed?: (accessToken: string) => void | Promise<void>;
  /** Called when a 401 could not be recovered by a refresh — force logout. */
  onUnauthorized?: () => void;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly problem: ProblemDetails,
  ) {
    super(problem.title);
    this.name = 'ApiError';
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Extra headers merged over the defaults (e.g. X-Client-Platform on mobile login). */
  headers?: Record<string, string>;
  /** Skip auto-refresh on 401. Used internally by the refresh call itself. */
  skipRefresh?: boolean;
  signal?: AbortSignal;
}

export interface ApiClient {
  apiFetch: <T>(path: string, opts?: RequestOptions) => Promise<T>;
  apiFetchBlob: (path: string, opts?: { signal?: AbortSignal; skipRefresh?: boolean }) => Promise<Blob>;
  setAccessToken: (token: string | null) => void;
  getAccessToken: () => string | null;
  setActiveOrganizationId: (id: string | null) => void;
  /** Platform-admin act-as: sends X-Admin-Act-As-Organization-Id so the server
   * scopes the whole session to the target client org. Null clears it. */
  setAdminActAsOrganizationId: (id: string | null) => void;
  setOnUnauthorizedHandler: (handler: (() => void) | null) => void;
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  const baseUrl = config.baseUrl.replace(/\/$/, '');
  const credentials: RequestCredentials = config.credentials ?? 'include';

  let inMemoryAccessToken: string | null = null;
  let activeOrganizationId: string | null = null;
  let adminActAsOrganizationId: string | null = null;
  let refreshInFlight: Promise<string | null> | null = null;
  let onUnauthorized: (() => void) | null = config.onUnauthorized ?? null;

  function authHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    if (inMemoryAccessToken) headers['Authorization'] = `Bearer ${inMemoryAccessToken}`;
    if (activeOrganizationId) headers['X-Active-Organization-Id'] = activeOrganizationId;
    if (adminActAsOrganizationId) headers['X-Admin-Act-As-Organization-Id'] = adminActAsOrganizationId;
    return headers;
  }

  /** Single-flight: concurrent 401s share one refresh request. */
  async function refresh(): Promise<string | null> {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async (): Promise<string | null> => {
      try {
        const init: RequestInit = { method: 'POST', credentials };
        if (config.getRefreshToken) {
          const refreshToken = await config.getRefreshToken();
          if (!refreshToken) return null;
          init.headers = {
            'Content-Type': 'application/json',
            'X-Client-Platform': 'mobile',
          };
          init.body = JSON.stringify({ refreshToken });
        }
        const res = await fetch(`${baseUrl}/auth/refresh`, init);
        if (!res.ok) return null;
        const data = (await res.json()) as { accessToken: string };
        inMemoryAccessToken = data.accessToken;
        await config.onTokensRefreshed?.(data.accessToken);
        return data.accessToken;
      } catch {
        return null;
      } finally {
        refreshInFlight = null;
      }
    })();
    return refreshInFlight;
  }

  async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const isFormData = typeof FormData !== 'undefined' && opts.body instanceof FormData;
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...authHeaders(),
      ...opts.headers,
    };
    if (opts.body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';

    const fetchInit: RequestInit = {
      method: opts.method ?? 'GET',
      headers,
      credentials,
    };
    if (opts.body !== undefined) {
      fetchInit.body = isFormData ? (opts.body as FormData) : JSON.stringify(opts.body);
    }
    if (opts.signal !== undefined) fetchInit.signal = opts.signal;
    const res = await fetch(`${baseUrl}${path}`, fetchInit);

    if (res.status === 401 && !opts.skipRefresh) {
      const refreshed = await refresh();
      if (refreshed) {
        return apiFetch<T>(path, { ...opts, skipRefresh: true });
      }
      onUnauthorized?.();
    }

    if (res.status === 204) {
      return undefined as T;
    }

    const text = await res.text();
    const payload = text ? (JSON.parse(text) as unknown) : null;

    if (!res.ok) {
      const problem = (payload ?? {
        type: 'urn:problem:unknown',
        title: res.statusText,
        status: res.status,
      }) as ProblemDetails;
      throw new ApiError(res.status, problem);
    }

    return payload as T;
  }

  /**
   * Same auth + base URL contract as apiFetch, but returns a Blob — used for
   * binary endpoints like /tts/preview where the response is audio/mpeg.
   */
  async function apiFetchBlob(
    path: string,
    opts: { signal?: AbortSignal; skipRefresh?: boolean } = {},
  ): Promise<Blob> {
    const init: RequestInit = { method: 'GET', headers: authHeaders(), credentials };
    if (opts.signal !== undefined) init.signal = opts.signal;
    const res = await fetch(`${baseUrl}${path}`, init);

    if (res.status === 401 && !opts.skipRefresh) {
      const refreshed = await refresh();
      if (refreshed) return apiFetchBlob(path, { ...opts, skipRefresh: true });
      onUnauthorized?.();
    }
    if (!res.ok) {
      let problem: ProblemDetails = {
        type: 'urn:problem:unknown',
        title: res.statusText,
        status: res.status,
      };
      try {
        problem = (await res.json()) as ProblemDetails;
      } catch {
        /* body wasn't JSON — keep the synthesised problem */
      }
      throw new ApiError(res.status, problem);
    }
    return res.blob();
  }

  return {
    apiFetch,
    apiFetchBlob,
    setAccessToken: (token) => {
      inMemoryAccessToken = token;
    },
    getAccessToken: () => inMemoryAccessToken,
    setActiveOrganizationId: (id) => {
      activeOrganizationId = id;
    },
    setAdminActAsOrganizationId: (id) => {
      adminActAsOrganizationId = id;
    },
    setOnUnauthorizedHandler: (handler) => {
      onUnauthorized = handler;
    },
  };
}
